<template>
  <div class="container-fluid">
    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
    </div>
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      {{ $t("ERRORS.SOMETHING_WENT_WRONG") }}
    </div>
    <div v-else>
      <div class="mb-4">
        <h4>{{ $t("WEEKLY_REPORTS.CASH_REGISTER_SUMMARY") }}</h4>
        <p>
          {{ $t("WEEKLY_REPORTS.DATE_RANGE") }}:
          {{ $timeZoneDateFormat(weeklyReport.starts_at, "LL") }} -
          {{ $timeZoneDateFormat(weeklyReport.ends_at, "LL") }}
        </p>
      </div>

      <div v-if="dailySummaries.length === 0" class="alert alert-info">
        {{ $t("WEEKLY_REPORTS.NO_CASH_REGISTER_READINGS") }}
      </div>

      <div v-else class="table-responsive">
        <table class="table table-bordered table-striped">
          <thead>
            <tr>
              <th>{{ $t("COMMON.DATE") }}</th>
              <th>{{ $t("CASH_REGISTER_READINGS.CASH_BALANCE") }}</th>
              <th>{{ $t("CASH_REGISTER_READINGS.CASHIN") }}</th>
              <!-- <th>{{ $t("CASH_REGISTER_READINGS.TOTAL_OF_SALES") }}</th> -->
              <!-- <th>{{ $t("CASH_REGISTER_READINGS.SALE_AMOUNT") }}</th> -->
              <th>{{ $t("CASH_REGISTER_READINGS.CASHOUT") }}</th>
              <th>{{ $t("CASH_REGISTER_READINGS.REGISTER_ERRORS") }}</th>
              <th>{{ $t("CASH_REGISTER_READINGS.COUPON_TO_INVOICE") }}</th>
              <th>
                {{ $t("CASH_REGISTER_READINGS.ACTUAL_RECEIVED_AMOUNT") }}
              </th>
              <th>{{ $t("CASH_REGISTER_READINGS.AMOUNT_GAP") }}</th>
              <th>{{ $t("CASH_REGISTER_READINGS.TRANSACTION_COUNT") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(summary, index) in dailySummaries" :key="index">
              <td>{{ $dateFormat(summary.date, "dddd LL") }}</td>
              <td>{{ $formatCurrency(summary.total_amount) }}</td>
              <td>{{ $formatCurrency(summary.amount_z) }}</td>
              <!-- <td>{{ $formatCurrency(summary.total_cash_in) }}</td> -->
              <!-- <td>{{ $formatCurrency(summary.sale_amount) }}</td> -->
              <td>{{ $formatCurrency(summary.expense_amount) }}</td>
              <td>{{ $formatCurrency(summary.register_errors) }}</td>
              <td>{{ $formatCurrency(summary.coupon_to_invoice_amount) }}</td>
              <td>{{ $formatCurrency(summary.actual_received_amount) }}</td>
              <td>{{ $formatCurrency(summary.amount_gap) }}</td>
              <td>{{ summary.transaction_count }}</td>
            </tr>
            <!-- Total row -->
            <tr class="font-weight-bold bg-light">
              <td>{{ $t("COMMON.TOTAL") }}</td>
              <td>{{ $formatCurrency(totals.total_amount) }}</td>
              <td>{{ $formatCurrency(totals.amount_z) }}</td>
              <!-- <td>{{ $formatCurrency(totals.total_cash_in) }}</td> -->
              <!-- <td>{{ $formatCurrency(totals.sale_amount) }}</td> -->
              <td>{{ $formatCurrency(totals.expense_amount) }}</td>
              <td>{{ $formatCurrency(totals.register_errors) }}</td>
              <td>{{ $formatCurrency(totals.coupon_to_invoice_amount) }}</td>
              <td>{{ $formatCurrency(totals.actual_received_amount) }}</td>
              <td>{{ $formatCurrency(totals.amount_gap) }}</td>
              <td>{{ totals.transaction_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from "date-fns";
import moment from "moment";
import { CASH_REGISTER_READING_STATUS_CANCELED } from "@/constants/cashRegisterReading";

export default {
  name: "weekly-report-view-cash-register-readings",

  props: {
    weeklyReport: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      error: false,
      cashRegisterReadings: [],
      dailySummaries: [],
      totals: {
        total_amount: 0,
        coupon_to_invoice_amount: 0,
        actual_received_amount: 0,
        total_cash_in: 0,
        sale_amount: 0,
        expense_amount: 0,
        amount_gap: 0,
        external_invoice_amount: 0,
        register_errors: 0,
        amount_z: 0,
        transaction_count: 0,
      },
    };
  },

  watch: {
    weeklyReport: {
      handler: "fetchCashRegisterReadings",
      immediate: true,
    },
  },

  methods: {
    async fetchCashRegisterReadings() {
      if (!this.weeklyReport.id || !this.weeklyReport.establishment) {
        this.loading = false;
        return;
      }

      this.loading = true;
      this.error = false;

      try {
        // Format date range for API query
        const startDate = format(
          new Date(this.weeklyReport.starts_at),
          "yyyy-MM-dd"
        );
        const endDate = format(
          new Date(this.weeklyReport.ends_at),
          "yyyy-MM-dd"
        );

        // Fetch cash register readings
        await this.$store.dispatch("cashRegisterReadings/list", {
          filter: {
            dateRange: [`${startDate} 00:00:00`, `${endDate} 23:59:59`],
            establishment: this.weeklyReport.establishment.id,
          },
          include: "establishment,cashRegister",
        });

        // Une lecture annulée ne compte dans aucun total.
        this.cashRegisterReadings = this.$store.getters[
          "cashRegisterReadings/list"
        ].filter(
          (reading) => reading.status !== CASH_REGISTER_READING_STATUS_CANCELED
        );

        // Process the readings into daily summaries
        this.processDailySummaries();
      } catch (error) {
        console.error(error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    processDailySummaries() {
      // Reset data
      this.dailySummaries = [];

      // Reset totals
      Object.keys(this.totals).forEach((key) => {
        this.totals[key] = 0;
      });

      if (!this.cashRegisterReadings.length) {
        return;
      }

      // Group readings by date
      const readingsByDate = {};

      this.cashRegisterReadings.forEach((reading) => {
        const dateKey = moment(reading.starts_at).format("YYYY-MM-DD");

        if (!readingsByDate[dateKey]) {
          readingsByDate[dateKey] = [];
        }

        readingsByDate[dateKey].push(reading);
      });

      // Calculate summary for each day
      Object.keys(readingsByDate)
        .sort()
        .forEach((dateKey) => {
          const readings = readingsByDate[dateKey];
          const summary = {
            date: dateKey,
            total_amount: 0,
            coupon_to_invoice_amount: 0,
            actual_received_amount: 0,
            total_cash_in: 0,
            sale_amount: 0,
            expense_amount: 0,
            amount_gap: 0,
            external_invoice_amount: 0,
            register_errors: 0,
            amount_z: 0,
            transaction_count: 0,
          };

          readings.forEach((reading) => {
            // Sum up the values
            summary.total_amount += Number(reading.total_amount || 0);
            summary.coupon_to_invoice_amount += Number(
              reading.coupon_to_invoice_amount || 0
            );
            summary.actual_received_amount += Number(
              reading.actual_received_amount || 0
            );
            summary.total_cash_in += Number(reading.total_cash_in || 0);
            summary.sale_amount += Number(reading.sale_amount || 0);
            summary.expense_amount += Number(reading.expense_amount || 0);
            summary.amount_gap += Number(reading.amount_gap || 0);
            summary.external_invoice_amount += Number(
              reading.external_invoice_amount || 0
            );
            summary.register_errors += Number(reading.register_errors || 0);
            summary.amount_z += Number(reading.amount_z || 0);
            summary.transaction_count += Number(reading.transaction_count || 0);
          });

          // Add to daily summaries
          this.dailySummaries.push(summary);

          // Add to totals
          Object.keys(this.totals).forEach((key) => {
            this.totals[key] += summary[key] || 0;
          });
        });
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
  padding: 0.5rem;
}
</style>